import React from 'react';
import { useTranslation } from 'react-i18next';
import { capitalize } from 'helpers/helpers';
import {
    Box,
    TextField,
    Typography,
    useTheme,
    RadioGroup,
    FormControlLabel,
    Radio,
    Autocomplete,
} from '@mui/material';
import {
    WidgetContent,
    WidgetT,
} from 'routes/Dashboard/components/WidgetsLayout/helpers';
import Flex from 'ui/Flex/Flex';
import ColorPicker from 'ui/ColorPicker/ColorPicker';
import { useEditNavLinkForm } from './hooks/navLinkForm';
import { useFetchActiveDashboards } from 'routes/AllDashboards/hooks/getDashboardsList';
import type { DashboardT } from 'business/definitions';
import GeneralModal from 'components/SharedComponents/Modals/GeneralModal';

type Props = {
    widget: WidgetT;
    isEditModalOpen: boolean;
    setIsEditModalOpen: (arg1: boolean) => void;
    onEditWidget: (arg1: { widget: WidgetT; content: WidgetContent }) => void;
};

export default function EditNavLinkModal({
    widget,
    isEditModalOpen,
    setIsEditModalOpen,
    onEditWidget,
}: Props): React.ReactElement {
    const { t } = useTranslation();
    const theme = useTheme();

    const { data: userDashboards = [] } = useFetchActiveDashboards();

    const {
        text,
        url,
        fontColor,
        backgroundColor,
        redirectType,
        dashboardSlug,
        errors,
        handleTextChange,
        handleUrlChange,
        handleFontColorChange,
        handleBackgroundColorChange,
        handleRedirectChange,
        handleSlugChange,
        validateFields,
    } = useEditNavLinkForm(widget, t, theme, userDashboards);
    const onConfirmAction = () => {
        if (validateFields()) {
            onEditWidget({
                widget,
                content: {
                    text,
                    fontColor,
                    backgroundColor,
                    ...(redirectType === 'url' ? { url } : { dashboardSlug }),
                },
            });
            setIsEditModalOpen(false);
        }
    };

    const dashboardValue = React.useMemo(() => {
        const found = userDashboards.find(
            (dashboard) => dashboard.slug === dashboardSlug,
        );
        if (found) return found;
        if (dashboardSlug) {
            return {
                slug: dashboardSlug,
                title: dashboardSlug,
                key: -1,
            } as DashboardT;
        }
        return null;
    }, [userDashboards, dashboardSlug]);

    const confirmButton = {
        text: t('widget.update'),
        onClick: onConfirmAction,
    };
    const cancelButton = {
        text: t('common.cancel'),
        onClick: () => setIsEditModalOpen(false),
    };
    return (
        <GeneralModal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            width="sm"
            textButton={cancelButton}
            containedButton={confirmButton}
            showCloseButton={false}
        >
            <Flex direction="column" gap={theme.spacing(2)}>
                <Typography variant="body1SemiBold">
                    {t('widget.navlink.sectionStyle')}
                </Typography>
                <Box mb={theme.spacing(1)}>
                    <TextField
                        value={text}
                        onChange={handleTextChange}
                        autoFocus
                        className="form-control"
                        variant="outlined"
                        margin="dense"
                        size="small"
                        fullWidth
                        key="text"
                        label={capitalize(t('widget.navlink.text'))}
                        name="title"
                        type="text"
                        error={!!errors.text}
                        helperText={errors.text}
                    />

                    <Flex
                        align="center"
                        marginTop={theme.spacing(1)}
                        gap={theme.spacing(2)}
                    >
                        <ColorPicker
                            value={fontColor}
                            setValue={handleFontColorChange}
                            label={t('widget.navlink.fontColor')}
                            error={errors.fontColor}
                        />
                        <ColorPicker
                            value={backgroundColor}
                            setValue={handleBackgroundColorChange}
                            label={t('widget.navlink.backgroundColor')}
                            error={errors.backgroundColor}
                        />
                    </Flex>
                </Box>
                <Typography variant="body1SemiBold">
                    {t('widget.navlink.sectionRedirection')}
                </Typography>
                <Flex direction="column" gap={theme.spacing(1)}>
                    <RadioGroup
                        row
                        value={redirectType}
                        onChange={handleRedirectChange}
                        name="redirectType"
                    >
                        <FormControlLabel
                            value="dashboard"
                            control={<Radio size="small" />}
                            label={capitalize(t('widget.navlink.redirectSlug'))}
                        />
                        <FormControlLabel
                            value="url"
                            control={<Radio size="small" />}
                            label={capitalize(t('widget.navlink.redirectUrl'))}
                        />
                    </RadioGroup>
                    {redirectType === 'url' ? (
                        <TextField
                            value={url}
                            onChange={handleUrlChange}
                            className="form-control"
                            variant="outlined"
                            margin="dense"
                            size="small"
                            fullWidth
                            key="url"
                            label={capitalize(t('widget.navlink.url'))}
                            name="url"
                            type="text"
                            error={!!errors.url}
                            helperText={errors.url}
                        />
                    ) : (
                        <Autocomplete
                            options={userDashboards}
                            getOptionLabel={(dashboard) => dashboard.title}
                            getOptionKey={(dashboard) => dashboard.key}
                            value={dashboardValue}
                            onChange={(_, newValue) =>
                                handleSlugChange(newValue?.slug ?? null)
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t('widget.navlink.dashboardName')}
                                    error={!!errors.slug}
                                    helperText={errors.slug}
                                />
                            )}
                        />
                    )}
                </Flex>
            </Flex>
        </GeneralModal>
    );
}
